import React, { ChangeEvent, FC, useCallback, useState } from 'react';
import Collapse from '@mui/material/Collapse';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Popover, { PopoverProps } from '@mui/material/Popover';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import debounce from 'lodash/debounce';

import { IDepartmentDTO, IHierarchicalDepartmentDTO } from 'src/api/dtos';
import CommonDepartmentRepository from 'src/api/repositories/common/v1/department.repository';
import { ChevronDownIcon, SearchIcon, Tooltip } from 'src/shared/components';
import { LocalStorageEnums } from 'src/shared/enums';
import { useLocalStorage } from 'src/shared/hooks';
import { useTranslate } from 'src/shared/providers';
import { formatDepartmentDisplay } from 'src/shared/utils';

import { DepartmentTreeView } from './DepartmentTreeView';

const RECENT_DEPARTMENTS_COUNT = 5;

interface Props extends Omit<PopoverProps, 'onSelect'> {
  selected?: IDepartmentDTO | null;
  onSelect?: (department: IHierarchicalDepartmentDTO) => void;
}

export const DepartmentPopover: FC<Props> = ({ selected, onSelect, open, ...props }) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const { value: recents, setValue: setRecents } = useLocalStorage<IHierarchicalDepartmentDTO[]>(
    LocalStorageEnums.TIMESHEET_RECENT_DEPARTMENTS,
    [] as IHierarchicalDepartmentDTO[],
  );

  const [inputValue, setInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [collapsed, setCollapsed] = useState(true);

  const isCollapseDisabled = !!searchTerm || !recents?.length;
  const isFullListOpen = !collapsed || isCollapseDisabled;

  const {
    data: departments,
    isLoading,
    isFetching,
  } = CommonDepartmentRepository.endpoints.useGetDepartmentsQuery(
    { searchTerm },
    {
      skip: !open,
      selectFromResult: ({ data, ...results }) => ({ data: data ?? [], ...results }),
    },
  );

  const loading = isLoading || isFetching;

  const debounceSearchTermChange = useCallback(
    debounce((value: string) => {
      setSearchTerm(value);
    }, 500),
    [],
  );

  const handleInputValueChange = (event: ChangeEvent<HTMLInputElement>) => {
    debounceSearchTermChange(event.target.value);
    setInputValue(event.target.value);
  };

  const handleSelect = (department: IHierarchicalDepartmentDTO) => {
    onSelect?.(department);
    setInputValue('');
    setSearchTerm('');

    if (recents?.some((recent) => recent.id === department.id)) {
      return;
    }

    setRecents([department, ...(recents ? recents : [])].slice(0, RECENT_DEPARTMENTS_COUNT));
  };

  return (
    <Popover open={open} {...props}>
      <Stack borderRadius={1} overflow="hidden">
        <TextField
          value={inputValue}
          onChange={handleInputValueChange}
          placeholder={t('global.selectOrSearchValue')}
          InputProps={{ endAdornment: <SearchIcon color={theme.palette.action.active} /> }}
          sx={{ padding: 1.5 }}
        />
      </Stack>
      <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" padding="0 12px">
        <Typography fontSize="0.75rem" fontWeight={600}>
          {t('global.recents')}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setCollapsed(!collapsed)}
          className={`collapsible-label ${!isFullListOpen ? 'open' : ''}`}
          disabled={isCollapseDisabled}
        >
          <ChevronDownIcon className="collapsible-chevron-icon" />
        </IconButton>
      </Stack>
      <Collapse in={collapsed && !searchTerm}>
        {!recents?.length && (
          <MenuItem disabled>
            <Typography fontSize="0.75rem">{t('global.none')}</Typography>
          </MenuItem>
        )}
        {recents?.map((recent) => {
          const label = formatDepartmentDisplay(recent.description, recent.number);

          return (
            <MenuItem key={recent.id} selected={recent.id === selected?.id} onClick={() => handleSelect(recent)}>
              <Tooltip title={<Typography fontSize="0.75rem">{label}</Typography>} showIcon={false}>
                <Typography fontSize="0.75rem" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
                  {label}
                </Typography>
              </Tooltip>
            </MenuItem>
          );
        })}
      </Collapse>
      <Divider />
      <Stack direction="row" gap={1} justifyContent="space-between" alignItems="center" padding="0 12px">
        <Typography fontSize="0.75rem" fontWeight={600}>
          {t('global.fullList')}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setCollapsed(!collapsed)}
          className={`collapsible-label ${isFullListOpen ? 'open' : ''}`}
          disabled={isCollapseDisabled}
        >
          <ChevronDownIcon className="collapsible-chevron-icon" />
        </IconButton>
      </Stack>
      <Collapse in={isFullListOpen}>
        <Stack maxHeight={300} overflow="auto" paddingBottom={1.5}>
          {loading ? (
            <Skeleton
              className="rounded"
              variant="rectangular"
              animation="wave"
              height={144}
              sx={{ margin: '0 8px' }}
            />
          ) : (
            <DepartmentTreeView selected={selected} options={departments} onSelect={handleSelect} />
          )}
        </Stack>
      </Collapse>
    </Popover>
  );
};
